/**
 * SectionErrorState — Generic error UI for drawer sections
 *
 * Displays a user-friendly error message when a section fails to load.
 * Nothing retries for you — the envelope hooks surface a failure exactly once —
 * so pass `onRetry` wherever the caller can re-run the failed query; without
 * it the user's only recourse is a full refresh.
 *
 * @example
 * ```tsx
 * {isError ? (
 *   <SectionErrorState onRetry={refetch} />
 * ) : data.length === 0 ? (
 *   <EmptyState />
 * ) : (
 *   <DataView />
 * )}
 * ```
 */

import { Button } from '#primitives/actions'

interface SectionErrorStateProps {
  /**
   * Optional custom message. The default depends on `onRetry`: with a retry
   * button, "Please refresh" would contradict it, so the copy defers to the
   * button instead.
   */
  message?: string
  /** When provided, renders a "Try again" button that invokes it. */
  onRetry?: () => void
}

export function SectionErrorState({ message, onRetry }: SectionErrorStateProps) {
  const text = message ?? (onRetry ? 'Something went wrong.' : 'Something went wrong. Please refresh')
  return (
    <div className="mt-4 rounded-lg border border-danger-edge bg-danger-subtle p-6 text-center">
      <p className="text-sm text-danger">{text}</p>
      {onRetry && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onRetry}
          // Token-clean styling: every Button variant paints literal
          // greys/reds/brand hexes that don't flip under `.dark`, so `ghost`
          // (the only variant with no resting fill/text) is the base and the
          // danger tokens are applied here. The `!` is required on the
          // interactive states — ghost's `data-[variant=ghost]:hover:*` rules
          // are attribute selectors that outrank plain utilities.
          className="mt-3 border border-danger-edge bg-surface text-danger text-interactive:!bg-surface-2 text-interactive:!text-danger"
        >
          Try again
        </Button>
      )}
    </div>
  )
}
